import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import axios from 'axios';
import { authSupabase } from './supabase/supabaseClient.js';

// Configuración de FileMaker para el sistema yooladmin
const FILEMAKER_CONFIG = {
  apiUrl: process.env.REACT_APP_FILEMAKER_API_URL,
  database: process.env.REACT_APP_FILEMAKER_DATABASE,
  token: process.env.REACT_APP_FILEMAKER_TOKEN
};

/**
 * Servicio de autenticación integrado con Clerk y FileMaker
 */
class AuthService {
  constructor() {
    this.sessionCache = new Map();
    this.configCache = null;
    this.configCacheExpiry = null;
  }

  /**
   * Crea o actualiza una sesión de usuario en FileMaker
   */
  async createUserSession(userData, sessionId) {
    try {
      const sessionData = {
        user_id: userData.id,
        clerk_user_id: userData.id,
        email: userData.primaryEmailAddress?.emailAddress,
        session_id: sessionId,
        login_time: new Date().toISOString(),
        last_activity: new Date().toISOString(),
        ip_address: await this.getUserIP(),
        user_agent: navigator.userAgent,
        is_active: true
      };

      this.sessionCache.set(userData.id, sessionData);
      await this.syncSessionToFileMaker(sessionData);
      return sessionData;
    } catch (error) {
      console.error('Error creating user session:', error);
      throw error;
    }
  }

  /**
   * Sincroniza datos de sesión con FileMaker
   */
  async syncSessionToFileMaker(sessionData) {
    try {
      const response = await axios.post(
        `${FILEMAKER_CONFIG.apiUrl}/fmi/data/vLatest/databases/${FILEMAKER_CONFIG.database}/layouts/user_sessions/records`,
        { fieldData: sessionData },
        {
          headers: {
            'Authorization': `Bearer ${FILEMAKER_CONFIG.token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error syncing session to FileMaker:', error);
    }
  }

  /**
   * Obtiene configuración del sistema desde FileMaker
   */
  async getSystemConfig() {
    try {
      if (this.configCache && this.configCacheExpiry && Date.now() < this.configCacheExpiry) {
        return this.configCache;
      }

      const response = await axios.get(
        `${FILEMAKER_CONFIG.apiUrl}/fmi/data/vLatest/databases/${FILEMAKER_CONFIG.database}/layouts/system_config_yoolusers/records`,
        {
          headers: {
            'Authorization': `Bearer ${FILEMAKER_CONFIG.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const config = {};
      response.data.response.data.forEach(record => {
        const fieldData = record.fieldData;
        config[fieldData.config_key] = {
          value: fieldData.config_value,
          type: fieldData.value_type,
          description: fieldData.description
        };
      });

      this.configCache = config;
      this.configCacheExpiry = Date.now() + 600000; // 10 minutos
      return config;
    } catch (error) {
      console.error('Error getting system config:', error);
      return this.getDefaultConfig();
    }
  }

  /**
   * Configuración por defecto
   */
  getDefaultConfig() {
    return {
      payment_retry_max_attempts: { value: '3', type: 'integer' },
      subscription_grace_period_days: { value: '7', type: 'integer' },
      maintenance_mode: { value: 'false', type: 'boolean' }
    };
  }

  /**
   * Obtiene la IP del usuario
   */
  async getUserIP() {
    try {
      const response = await axios.get('https://api.ipify.org?format=json');
      return response.data.ip;
    } catch (error) {
      return 'unknown';
    }
  }
}

const authService = new AuthService();

// Función para crear sesión anónima (Supabase - mantenida para compatibilidad)
export const signInAnonymously = async () => {
  try {
    const { data, error } = await authSupabase.auth.signInAnonymously();
    
    if (error) {
      console.error('Error al crear usuario anónimo:', error);
      return { success: false, error };
    }
    
    console.log('Usuario anónimo creado:', data);
    return { success: true, user: data.user };
  } catch (error) {
    console.error('Error inesperado:', error);
    return { success: false, error };
  }
};

// Función para verificar si hay sesión activa
export const getCurrentUser = async () => {
  try {
    const { data: { user } } = await authSupabase.auth.getUser();
    return user;
  } catch (error) {
    console.error('Error al obtener usuario actual:', error);
    return null;
  }
};

// Función para cerrar sesión (Supabase - mantenida para compatibilidad)
export const signOut = async () => {
  try {
    const { error } = await authSupabase.auth.signOut();
    if (error) {
      console.error('Error al cerrar sesión:', error);
    }
    return !error;
  } catch (error) {
    console.error('Error inesperado al cerrar sesión:', error);
    return false;
  }
};

// Nuevas funciones para Clerk y FileMaker
export const useAuthService = () => {
  const { user } = useUser();
  const { isSignedIn, isLoaded } = useAuth();
  const clerk = useClerk();

  return {
    user,
    isSignedIn,
    isLoaded,
    clerk,
    authService
  };
};

export const createSession = async (userData, sessionId) => {
  return await authService.createUserSession(userData, sessionId);
};

export const getConfig = async () => {
  return await authService.getSystemConfig();
};

export default authService;
